import * as React from 'react';
import type { ResolveThunks } from 'react-redux';
import { connect } from 'react-redux';
import { formValueSelector, change } from 'redux-form';

import type { ShiftViewModel } from 'ab-viewModels/shift.viewModel';

import { ShiftItems } from '@acceligentllc/shared/enums/shift';

import type { RootState } from 'af-reducers';

import { WORK_ORDER_FORM } from 'af-constants/reduxForms';

import RadioGroup from 'af-fields/RadioGroup';

import * as ShiftActions from 'af-actions/shift';

interface Item {
	label: string;
	value: string | number;
}

interface OwnProps {
	disabled: boolean;
	onValueChange?: (newValue: Nullable<Item>) => void;
}

interface StateProps {
	selector: (prop: string) => string | number;
}

interface DispatchProps {
	createShift: typeof ShiftActions.create;
	findShifts: typeof ShiftActions.findByQuery;
	changeField: typeof change;
}

type Props = OwnProps & StateProps & ResolveThunks<DispatchProps>;

const Shift: React.FC<Props> = (props) => {

	const {
		findShifts,
		changeField,
		createShift,
		onValueChange,
		selector,
		disabled,
	} = props;

	const onSearch = React.useCallback(async (text: string) => {
		return await findShifts(text);
	}, [findShifts]);

	const onNewShift = React.useCallback(async (name: string): Promise<void> => {
		const shift = await createShift(name);
		changeField(WORK_ORDER_FORM, 'shift', shift);
	}, [changeField, createShift]);

	const changeShift = React.useCallback((field: string, value: number | string) => {
		changeField(WORK_ORDER_FORM, field, value);
	}, [changeField]);

	const getOptionValue = React.useCallback((option: ShiftViewModel) => `${option.id}`, []);

	const getOptionLabel = React.useCallback((option: ShiftViewModel) => option.name, []);

	const formatOptionLabel = React.useCallback((option: ShiftViewModel) => <span>{option.name}</span>, []);

	const isValidNewOption = React.useCallback((inputValue: string, value: ShiftViewModel, options: ShiftViewModel[]) => {
		return !!inputValue && !options.some(({ name }) => name.toLowerCase() === inputValue.toLowerCase());
	}, []);

	return (
		<div className="work-order-upsert__shift">
			<span>Shift *</span>
			<div className="work-order-upsert__shift-container">
				{
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					<RadioGroup<any>
						changeField={changeShift}
						disabled={disabled}
						field="shift"
						formatOptionLabel={formatOptionLabel}
						getOptionLabel={getOptionLabel}
						getOptionValue={getOptionValue}
						inline={true}
						isValidNewOption={isValidNewOption}
						items={ShiftItems}
						labelKey="name"
						onNew={onNewShift}
						onValueChange={onValueChange}
						placeholder="Enter Shift *"
						searchOtherOptions={onSearch}
						selector={selector}
						valueKey="id"
						withOther={true}
					/>
				}
			</div>
		</div>
	);
};

function mapStateToProps(state: RootState): StateProps {
	return {
		selector: (prop: string) => formValueSelector(WORK_ORDER_FORM)(state, prop),
	};
}

function mapDispatchToProps(): DispatchProps {
	return {
		changeField: change,
		createShift: ShiftActions.create,
		findShifts: ShiftActions.findByQuery,
	};
}

export default connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps())(Shift);
